<template>
  <section class="disclosed-operation" v-loading="loading">
    <div class="report-toolbar">
      <strong>运营数据</strong>
      <el-select v-model="period" placeholder="选择报告期" @change="loadReport" :disabled="!periods.length">
        <el-option v-for="item in periods" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select v-if="categories.length > 1" v-model="category" placeholder="选择业态" @change="loadMetricOptions">
        <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
      </el-select>
      <el-select v-if="Object.keys(factors).length" v-model="factor" placeholder="选择指标" @change="loadReport">
        <el-option v-for="(label, key) in factors" :key="key" :label="label" :value="key" />
      </el-select>
      <el-button v-if="error" size="small" @click="loadPeriods">重新加载</el-button>
      <el-button v-if="period" size="small" @click="toggleSource">{{ showSource ? '收起追源' : '查看追源' }}</el-button>
    </div>
    <div v-if="error" class="report-error" role="alert">{{ error }}</div>
    <template v-else-if="period">
      <el-table :data="rows" style="width:100%">
        <el-table-column prop="asset_name" label="资产" min-width="240" />
        <el-table-column :label="(factors[factor] || '指标') + (numberType === 'pct' ? '（%）' : '')" min-width="180">
          <template slot-scope="scope">{{ formatValue(scope.row[period]) }}</template>
        </el-table-column>
        <el-table-column label="报告期" min-width="120"><template>{{ period }}</template></el-table-column>
      </el-table>
      <div v-if="notes[factor]" class="metric-note" v-html="notes[factor]"></div>
    </template>
    <p v-else-if="!loading">当前基金暂无已披露的运营数据。</p>
    <div v-if="showSource" v-loading="sourceLoading">
      <div v-if="sourceError" class="report-error" role="alert">{{ sourceError }}</div>
      <iframe v-else-if="html" title="运营数据追源" :srcdoc="html" sandbox="allow-scripts" />
    </div>
  </section>
</template>

<script>
export default {
  name: 'DisclosedOperationReport',
  props: { code: { type: String, required: true } },
  data() {
    return {
      periods: [], period: '', categories: [], category: '', factors: {}, factor: '', notes: {},
      rows: [], numberType: '', loading: false, error: '', request: 0,
      html: '', showSource: false, sourceLoading: false, sourceError: '', sourceRequest: 0,
    };
  },
  watch: { code: { immediate: true, handler() { this.loadPeriods(); } } },
  beforeDestroy() { this.request += 1; this.sourceRequest += 1; },
  methods: {
    async loadPeriods() {
      const request = ++this.request;
      this.periods = []; this.period = ''; this.rows = []; this.error = '';
      this.categories = []; this.category = ''; this.factors = {}; this.factor = ''; this.notes = {};
      this.sourceRequest += 1; this.showSource = false; this.html = ''; this.sourceError = '';
      if (!this.code) { this.loading = false; return; }
      this.loading = true;
      try {
        const { data } = await this.$https.get('/api/v4/single_operation_html_periods', { params: { code: this.code } });
        if (request !== this.request) return;
        if (data.code !== 200) throw new Error(data.msg || '运营报告期加载失败');
        this.periods = (data.data || {}).periods || [];
        this.categories = [...new Set(String((data.data || {}).category || '').split('、').map(s => s.trim()).filter(Boolean))];
        this.period = this.periods[0] || ''; this.category = this.categories[0] || '';
        if (this.period && this.category) await this.loadMetricOptions();
      } catch (error) {
        if (request === this.request) this.error = error.message || '运营报告期加载失败，请重试';
      } finally {
        if (request === this.request) this.loading = false;
      }
    },
    async loadMetricOptions() {
      const request = ++this.request;
      this.rows = []; this.factors = {}; this.factor = ''; this.notes = {}; this.error = ''; this.loading = true;
      try {
        const { data } = await this.$https.get('/api/v4/get_operation_data_button', { params: { category_2: this.category } });
        if (request !== this.request) return;
        if (data.code !== 200) throw new Error(data.msg || '运营指标加载失败');
        this.factors = (data.data || {}).factor_names || {}; this.notes = (data.data || {}).factor_maps || {};
        this.factor = Object.prototype.hasOwnProperty.call(this.factors, 'point_occ') ? 'point_occ' : Object.keys(this.factors)[0] || '';
        if (this.factor) await this.loadReport();
      } catch (error) {
        if (request === this.request) this.error = error.message || '运营指标加载失败，请重试';
      } finally {
        if (request === this.request) this.loading = false;
      }
    },
    async loadReport() {
      const request = ++this.request;
      this.rows = []; this.error = '';
      if (!this.code || !this.period || !this.factor) { this.loading = false; return; }
      this.loading = true;
      if (this.showSource) this.loadSource();
      try {
        const { data } = await this.$https.get('/api/v4/get_operation_data_all', {
          params: { category_2: this.category, codes: this.code, factor_name: this.factor },
        });
        if (request !== this.request) return;
        if (data.code !== 200) throw new Error(data.msg || '运营数据加载失败');
        this.rows = (data.data || {}).data || []; this.numberType = (data.data || {}).col_num_type || '';
      } catch (error) {
        if (request === this.request) this.error = error.message || '运营数据加载失败，请重试';
      } finally {
        if (request === this.request) this.loading = false;
      }
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—';
      if (typeof value !== 'number' || !Number.isFinite(value)) return typeof value === 'string' ? value : '—';
      const number = this.numberType === 'pct' ? value * 100 : value;
      return number.toLocaleString('zh-CN', { maximumFractionDigits: this.numberType === 'pct' ? 2 : 4 });
    },
    toggleSource() {
      this.showSource = !this.showSource;
      if (this.showSource) this.loadSource();
    },
    async loadSource() {
      const request = ++this.sourceRequest;
      this.html = ''; this.sourceError = ''; this.sourceLoading = true;
      try {
        const { data } = await this.$https.get('/api/v4/single_operation_html', {
          params: { code: this.code, rp_period: this.period, html_variant: 'original' },
        });
        if (request !== this.sourceRequest) return;
        if (data.code !== 200) throw new Error(data.msg || '追源内容加载失败');
        this.html = (data.data || {}).display_html || '';
      } catch (error) {
        if (request === this.sourceRequest) this.sourceError = error.message || '追源内容加载失败，请重试';
      } finally {
        if (request === this.sourceRequest) this.sourceLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.report-error { padding: 12px; border: 1px solid currentColor; margin-bottom: 16px; }
.report-toolbar { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; margin-bottom: 16px; }
.report-toolbar .el-select { width: 200px; }
.report-toolbar strong { white-space: nowrap; }
.metric-note { margin: 16px 0; line-height: 1.8; }
.disclosed-operation iframe { width: 100%; min-height: 720px; border: 0; background: white; }
</style>
